import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  TemplateRef,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../../components/modal-escape-guard/modal-escape-guard.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TestModuleService } from '../../../services/test-module.service';
import { TEST_MODULE_STATUSES, TestModule, TestModuleStatus } from '../../../models/test-module.model';

type StatusFilter = TestModuleStatus | 'ALL';

/**
 * Modul-Einbinden-Dialog (NEX-465, Design page-module.jsx:39-127): listet die
 * anderen Module des Projekts (eigenes Modul + bereits eingebundene werden
 * ausgeblendet), erlaubt Suche, Status- und Autor-Filter sowie Mehrfachauswahl
 * und gibt die gewaehlten Module via {@link applied} zurueck.
 */
@Component({
  selector: 'app-module-embed-dialog',
  imports: [Modal, ModalEscapeGuardDirective, TranslatePipe, MatIcon, MatProgressSpinner],
  templateUrl: './module-embed-dialog.component.html',
  styleUrl: './module-embed-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleEmbedDialogComponent implements AfterViewInit {
  private readonly moduleService = inject(TestModuleService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private loaded = false;

  readonly projectId = input.required<string>();
  /** IDs, die nicht wählbar sein dürfen: eigenes Modul + bereits eingebundene. */
  readonly excludeIds = input<readonly string[]>([]);
  readonly visible = model(false);

  readonly applied = output<TestModule[]>();

  @ViewChild('embedContent') embedContentTemplate!: TemplateRef<unknown>;
  resolvedTemplate: TemplateRef<unknown> | null = null;

  readonly statusOptions: readonly StatusFilter[] = ['ALL', ...TEST_MODULE_STATUSES];

  readonly modules = signal<TestModule[]>([]);
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly searchTerm = signal('');
  readonly statusFilter = signal<StatusFilter>('ALL');
  readonly authorFilter = signal('');
  readonly selectedIds = signal<ReadonlySet<string>>(new Set());

  private readonly available = computed(() => {
    const exclude = new Set(this.excludeIds());
    return this.modules().filter((m) => !exclude.has(m.id));
  });

  readonly authorOptions = computed(() => {
    const authors = new Map<string, string>();
    for (const m of this.available()) {
      if (m.authorUserId && !authors.has(m.authorUserId)) {
        authors.set(m.authorUserId, m.authorName || m.authorUserId);
      }
    }
    return Array.from(authors, ([id, name]) => ({ id, name }));
  });

  readonly filtered = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const status = this.statusFilter();
    const author = this.authorFilter();
    return this.available().filter(
      (m) =>
        (!term || m.name.toLowerCase().includes(term) || m.key.toLowerCase().includes(term)) &&
        (status === 'ALL' || m.status === status) &&
        (!author || m.authorUserId === author),
    );
  });

  readonly selectedCount = computed(() => this.selectedIds().size);
  readonly canApply = computed(() => this.selectedCount() > 0);

  constructor() {
    effect(() => {
      if (!this.visible()) return;
      untracked(() => {
        this.reset();
        if (!this.loaded) {
          this.loaded = true;
          this.loadModules();
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.resolvedTemplate = this.embedContentTemplate;
    this.cdr.detectChanges();
  }

  onSearch(event: Event): void {
    this.searchTerm.set((event.target as HTMLInputElement).value);
  }

  onAuthorChange(event: Event): void {
    this.authorFilter.set((event.target as HTMLSelectElement).value);
  }

  isSelected(moduleId: string): boolean {
    return this.selectedIds().has(moduleId);
  }

  toggle(moduleId: string): void {
    this.selectedIds.update((current) => {
      const next = new Set(current);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  }

  onApply(): void {
    if (!this.canApply()) {
      return;
    }
    const selected = this.selectedIds();
    this.applied.emit(this.modules().filter((m) => selected.has(m.id)));
    this.close();
  }

  onCancel(): void {
    this.close();
  }

  private close(): void {
    this.visible.set(false);
    this.reset();
  }

  private reset(): void {
    this.searchTerm.set('');
    this.statusFilter.set('ALL');
    this.authorFilter.set('');
    this.selectedIds.set(new Set());
  }

  private loadModules(): void {
    this.loading.set(true);
    this.error.set(false);
    this.moduleService
      .list(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (modules) => {
          this.modules.set(modules);
          this.loading.set(false);
        },
        error: () => {
          this.error.set(true);
          this.loading.set(false);
        },
      });
  }
}
